import type { ReactNode } from 'react';
import type { DeliveryScope, DeliveryTiles, Metric } from '../../api/types.js';
import {
  color,
  featuredPanel,
  font,
  fontSize,
  initialsOf,
  personAccent,
  radius,
  seriesColor,
} from '../../theme/tokens.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import { ProvenanceFootnote } from '../Breakdown/Breakdown.js';
import { provenanceFor } from '../Breakdown/provenance.js';
import { formatValue } from '../MetricCell/MetricCell.js';

/**
 * The selected-entity header (FR-D07, PRD-Delivery §6.3, §6.4).
 *
 * Avatar, name, role chip, context sub-line, and a **six**-tile strip whose set varies by scope.
 * The strip renders its own value rather than delegating to `<MetricCell>` for the same reason the
 * Region Detail summary bar does: these cells are large statistics with no trend arrow, where
 * `<MetricCell>` renders one number and one arrow.
 *
 * The tooltip still routes through the shared `<Disclosure>`, so hover, keyboard focus, and the
 * accessible name all carry the same content (FR-D15, FR-062) — a bespoke hover div would be
 * pointer-only.
 *
 * ⚠ **The manager strip's sixth tile is Onboarding, not Flagged**, and there is no health status
 * line anywhere in this component. Book-health flagging is out of scope with recorded evidence
 * (FR-D17): the blueprint's thresholds flag 131 of 134 recruiters, and an indicator that fires on
 * 98% of people teaches managers to ignore it before it can ever mean anything.
 */

interface Tile {
  key: keyof DeliveryTiles;
  label: string;
  /** The announced name and the tooltip — the cell shows a short label, this says what it is. */
  full: string;
  color?: string;
  /** Rendered as `64.2%` rather than `64.2`. */
  percent?: boolean;
  /**
   * A headcount that does **not** follow `lens` — so the lens phrase is kept out of its
   * accessible name, which would otherwise claim a figure answers to controls it ignores.
   *
   * Marked per set rather than per key, because the same tile is not the same figure in both:
   * the org strip's Recruiters is the total of the `Rec` column below it and narrows with
   * Specialty exactly as that column does, while a manager's Recruiters is the **size of the
   * list** below it and the denominator of that list's `N of M` header (see `tilesForOrg` and
   * `tilesForManager` in `api/delivery.ts`, which is where the two part company).
   */
  structural?: boolean;
}

/**
 * Prospect's violet, held here rather than borrowed from `seriesColor.offers`.
 *
 * It *was* that token, and read the same, until OFFERS moved to magenta on 2026-08-28 to pull
 * clear of TOA on the chart. Prospect is not a chart series and had no reason to move with it,
 * so the literal is the point: these two hues matched by coincidence, not by meaning, and the
 * alias would have dragged this tile along with a decision about a line on a graph.
 */
const PROSPECT_VIOLET = color.colProspectAlt;

/**
 * The three sets, in the blueprint's order.
 *
 * Active TOA blue, Pending amber, Prospect violet, Conversion teal — the same semantic hues the
 * Regions page uses, so a colour means one thing across the product.
 */
const TILE_SETS: Record<string, Tile[]> = {
  all: [
    { key: 'recruiters', label: 'Recruiters', full: 'Recruiters in scope' },
    {
      key: 'managers',
      label: 'Managers',
      full: 'Managers with an in-scope team',
      // The table below lists every manager whatever the filters say — their metric columns
      // narrow, their rows do not — so this figure has no lens to follow.
      structural: true,
    },
    { key: 'bob', label: 'Book of Business', full: 'Total Candidates in BoB' },
    { key: 'activeToa', label: 'Active TOA', full: 'Active TOA — Travelers on Assignment, as-of', color: seriesColor.toa },
    { key: 'pending', label: 'Pending', full: 'Candidates Pending', color: seriesColor.jobs },
    { key: 'prospect', label: 'Prospect', full: 'Candidates Prospect', color: PROSPECT_VIOLET },
  ],
  manager: [
    { key: 'recruiters', label: 'Recruiters', full: 'Recruiters on this team', structural: true },
    { key: 'bob', label: 'Book of Business', full: 'Total Candidates in BoB' },
    { key: 'activeToa', label: 'Active TOA', full: 'Active TOA — Travelers on Assignment, as-of', color: seriesColor.toa },
    // Onboarding, NOT Flagged (FR-D17).
    { key: 'onboarding', label: 'Onboarding', full: 'Candidates Onboarding' },
    { key: 'pending', label: 'Pending', full: 'Candidates Pending', color: seriesColor.jobs },
    { key: 'prospect', label: 'Prospect', full: 'Candidates Prospect', color: PROSPECT_VIOLET },
  ],
  recruiter: [
    { key: 'bob', label: 'Book of Business', full: 'Total Candidates in BoB' },
    { key: 'activeToa', label: 'Active TOA', full: 'Active TOA — Travelers on Assignment, as-of', color: seriesColor.toa },
    { key: 'onboarding', label: 'Onboarding', full: 'Candidates Onboarding' },
    { key: 'pending', label: 'Pending', full: 'Candidates Pending', color: seriesColor.jobs },
    { key: 'prospect', label: 'Prospect', full: 'Candidates Prospect', color: PROSPECT_VIOLET },
    {
      key: 'conversion',
      label: 'Conversion',
      full: 'Conversion % — offers divided by submissions',
      color: color.nursing,
      percent: true,
    },
  ],
};

/** Leader and All share a strip; the other two states have their own. */
function tilesFor(scopeType: DeliveryScope['type']): Tile[] {
  if (scopeType === 'manager') return TILE_SETS.manager!;
  if (scopeType === 'recruiter') return TILE_SETS.recruiter!;
  return TILE_SETS.all!;
}

export interface EntityHeaderProps {
  scope: DeliveryScope;
  tiles: DeliveryTiles;
  /** The back control. Rendered only where the scope names somewhere to go back to. */
  onBack?: () => void;
  /**
   * The chart, folded into this card between the identity row and the tile strip (owner request
   * 2026-09-03). One card, so that the strip's figures and the chart's filters read as one
   * statement about one entity rather than two panels that happen to be stacked.
   *
   * ⚠ The chart sits **above** the strip on purpose: its own header row is where the Source /
   * Targeting / Specialty controls live, and those controls now move the strip too, so they have
   * to be visible above the figures they govern. Putting the strip first buries the controls
   * under the numbers they change, which is the arrangement FR-034 is about.
   */
  chart?: ReactNode;
  /**
   * The lens the strip's figures are read at, named on the card face — e.g.
   * `All sources · Target · Nursing`.
   *
   * Required whenever `chart` is passed, and for the reason the Submissions card names its
   * window (PRD §9.7.6): a figure that moves when a chart control is touched, with nothing on
   * screen saying it would, reads as a fault. It is folded into each lensed tile's accessible
   * name as well, so the pointer and the screen reader are told the same thing.
   */
  lens?: string;
  /** Featured chrome — the treatment every card carrying a chart gets (owner request 2026-08-30). */
  featured?: boolean;
  /** Extra content beneath the strip — the recruiter state's target accounts. */
  children?: ReactNode;
}

export function EntityHeader({
  scope,
  tiles,
  onBack,
  chart,
  lens,
  featured,
  children,
}: EntityHeaderProps) {
  const accent = personAccent(scope.key);
  const set = tilesFor(scope.type);

  return (
    <section
      aria-label={`${scope.name} summary`}
      style={{
        ...(featured ? featuredPanel : { background: color.surface, border: `1px solid ${color.border}` }),
        // The accent stays whatever the chrome is: it is how a person is identified on this page,
        // not part of the featured treatment.
        borderLeft: `4px solid ${accent}`,
        borderRadius: radius['6xl'],
        /*
         * 20px across, not the 24 this card carried until 2026-09-11, and the four pixels are
         * the chart's **matrix** view (PRD §6.2): that table is a budget — 100 + 12×74 + 88 =
         * 1,076px — measured against a chart card's interior, and this card is the narrowest one
         * in the product because it also spends 3px on the accent rail. At 24px it had 1,069px
         * of grid room at a 1,280 viewport with the rail collapsed, so the twelve buckets
         * scrolled by a few pixels on the one page whose chart is folded into an entity card. At
         * 20 it has 1,077 and matches the region chart cards it is a second reading of.
         *
         * ⚠ Symmetric on purpose: bleeding the chart block alone past the padding would have
         * bought the same pixels and left the plot's left edge out of line with the heading
         * above it and the tile grid below.
         */
        padding: '20px 20px',
        marginBottom: 16,
      }}
    >
      {scope.backTo && onBack ? (
        <button
          type="button"
          onClick={onBack}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: fontSize.captionStrong,
            fontWeight: 500,
            color: color.inkMuted,
            background: 'none',
            border: 'none',
            padding: 0,
            marginBottom: 12,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          {scope.backTo.type === 'all' ? 'All managers' : `${scope.backTo.name}’s team`}
        </button>
      ) : null}

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div
          aria-hidden="true"
          style={{
            width: 48,
            height: 48,
            flex: 'none',
            borderRadius: radius['4xl'],
            background: accent,
            color: color.onAccent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          {initialsOf(scope.name)}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2
              style={{
                margin: 0,
                fontSize: fontSize.detailTitle,
                fontWeight: 700,
                letterSpacing: '-.4px',
              }}
            >
              {scope.name}
            </h2>
            <span
              style={{
                fontSize: fontSize.caption,
                fontWeight: 600,
                color: color.inkMuted,
                background: color.surfaceChip,
                border: `1px solid ${color.borderChip}`,
                padding: '3px 10px',
                borderRadius: radius.pill,
              }}
            >
              {scope.role}
            </span>
          </div>
          <div style={{ fontSize: fontSize.captionStrong, color: color.inkSubtle, marginTop: 3 }}>
            {scope.sub}
          </div>
        </div>
      </div>

      {chart ? (
        <div
          style={{
            marginTop: 16,
            paddingTop: 16,
            borderTop: `1px solid ${color.borderSubtle}`,
          }}
        >
          {chart}
        </div>
      ) : null}

      {lens ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            flexWrap: 'wrap',
            marginTop: 18,
            paddingTop: 14,
            borderTop: `1px solid ${color.borderSubtle}`,
          }}
        >
          <span
            style={{
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '.3px',
              color: color.inkFaint,
              textTransform: 'uppercase',
            }}
          >
            Follows the chart
          </span>
          <span style={{ fontSize: fontSize.caption, fontWeight: 600, color: color.inkMuted }}>
            {lens}
          </span>
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, minmax(90px, 1fr))',
          gap: 1,
          background: color.borderSubtle,
          border: `1px solid ${color.borderSubtle}`,
          borderRadius: radius['3xl'],
          overflow: 'hidden',
          marginTop: lens ? 10 : 16,
        }}
      >
        {set.map((tile) => {
          const metric: Metric | null | undefined = tiles[tile.key];
          const value = metric?.value ?? null;
          const display = tile.percent
            ? value === null
              ? formatValue(null)
              : `${formatValue(value)}%`
            : formatValue(value);

          return (
            <div key={tile.key} style={{ background: color.surface, padding: '12px 14px' }}>
              <div
                style={{
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '.3px',
                  color: color.inkFaint,
                  textTransform: 'uppercase',
                }}
              >
                {tile.label}
              </div>
              <Disclosure
                accessibleName={`${tile.full}${
                  lens && !tile.structural ? ` · ${lens}` : ''
                }: ${display}`}
                /*
                 * The tile said only its own name until 2026-09-10 — the panel defaulted to the
                 * accessible name — so six of the most prominent figures on the Delivery page
                 * were the ones a reader could learn least about. The lens stays out of the
                 * footer deliberately: it is already on the card face and in the name above, and
                 * the footer states how the figure is *counted*, which a lens does not change.
                 */
                panel={
                  <span style={{ display: 'block' }}>
                    <strong>{tile.full}</strong>
                    <span style={{ display: 'block' }}>{display}</span>
                    <ProvenanceFootnote provenance={provenanceFor(tile.key)} />
                  </span>
                }
              >
                <span
                  className="mono"
                  style={{
                    fontFamily: font.mono,
                    fontSize: fontSize.statValue,
                    fontWeight: 600,
                    color: tile.color ?? color.ink,
                  }}
                >
                  {display}
                </span>
              </Disclosure>
            </div>
          );
        })}
      </div>

      {children ? <div style={{ marginTop: 16 }}>{children}</div> : null}
    </section>
  );
}
